import { apiClient } from '@/lib/apiClient';
import { pushDataLayerEvent } from '@/lib/data-layer';
import { interpret as interpretRoute } from '@/routes/search';
import { type SearchType, type SmartSearchInterpretResponse, type SmartSearchSettings } from '@/types/search-criteria';

/** Query parameter with the phrase of the smart search (SearchController::SMART_QUERY_PARAM). */
export const SMART_QUERY_PARAM = 'ai_query';

/** Query flag set when the AI could not turn the phrase into filters (SearchController::SMART_MISS_PARAM). */
export const SMART_MISS_PARAM = 'ai_miss';

export const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
    embarcacoes: 'Embarcações',
    empresas: 'Empresas',
};

export const SEARCH_TYPE_TITLES: Record<SearchType, string> = {
    embarcacoes: 'Que embarcação você procura?',
    empresas: 'Que serviço ou empresa você procura?',
};

export const SEARCH_TYPE_PLACEHOLDERS: Record<SearchType, string> = {
    embarcacoes: 'Ex.: lancha para pernoite até 300 mil em SC',
    empresas: 'Ex.: mecânico em Angra, curso de arrais, marina',
};

/** Where the smart search was sent from (GA4 "origem"). */
export type SmartSearchOrigin = 'home' | 'topo' | 'lupa' | 'resultados';

export const DEFAULT_SMART_SEARCH_SETTINGS: SmartSearchSettings = {
    enabled: { embarcacoes: false, empresas: false },
    example_phrases: { embarcacoes: [], empresas: [] },
    tab_hint_words: { embarcacoes: [], empresas: [] },
    shortcuts: { embarcacoes: [], empresas: [] },
    max_text_length: 200,
};

export const otherSearchType = (type: SearchType): SearchType => (type === 'empresas' ? 'embarcacoes' : 'empresas');

/**
 * Interprets the phrase on the server. Any failure (network, 429, 500) becomes the plain text search: the visitor
 * never sees an error (spec item 3). Returns null only for an empty phrase.
 */
export const interpretSearch = async (text: string, type: SearchType): Promise<SmartSearchInterpretResponse | null> => {
    const phrase = text.trim();

    if (!phrase) {
        return null;
    }

    try {
        const response = await apiClient.post<SmartSearchInterpretResponse>(interpretRoute.url(), { text: phrase, type });

        return response.data;
    } catch (error: unknown) {
        console.error('Smart search unavailable, falling back to the text search:', error);

        return {
            type,
            text: phrase,
            status: 'unavailable',
            understood: false,
            criteria: null,
            url: buildTextSearchUrl(phrase, type),
            result_count: null,
            from_cache: false,
        };
    }
};

export const buildTextSearchUrl = (text: string, type: SearchType): string => {
    const params = new URLSearchParams({ type, search: text.trim() });

    return `/search?${params.toString()}`;
};

const normalizeWord = (value: string) =>
    value
        .toLocaleLowerCase('pt-BR')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim();

/**
 * "Wrong tab" hint without AI (spec 5.3): a configurable word of the other tab found in the phrase. Whole words only,
 * accents ignored ("mecânico" matches "mecanico").
 */
export const detectWrongTab = (text: string, type: SearchType, settings: SmartSearchSettings): SearchType | null => {
    const phrase = ` ${normalizeWord(text).replace(/[^\p{L}\p{N}]+/gu, ' ')} `;

    if (phrase.trim() === '') {
        return null;
    }

    const words = settings.tab_hint_words[type] ?? [];
    const hit = words.some((word) => {
        const normalized = normalizeWord(word).replace(/[^\p{L}\p{N}]+/gu, ' ');

        return normalized !== '' && phrase.includes(` ${normalized} `);
    });

    return hit ? otherSearchType(type) : null;
};

/**
 * GA4 events of the search (spec item 9), always with the type. Never send the phrase (free text can hold personal data).
 */
export const trackSearchEvent = (
    event:
        | 'busca_ia_enviada'
        | 'busca_ia_exemplo_click'
        | 'busca_ia_nao_entendida'
        | 'busca_etiqueta_removida'
        | 'busca_filtros_abertos'
        | 'busca_ver_resultados_click'
        | 'busca_sugestao_zero_click'
        | 'busca_aba_trocada'
        | 'busca_aviso_aba_click'
        | 'topo_busca_aberta'
        | 'parecidas_etiqueta_removida'
        | 'parecidas_ver_todas',
    type: SearchType,
    params: Record<string, string | number | boolean | null> = {},
): void => {
    pushDataLayerEvent(event, { tipo: type, ...params });
};

/** Shortcut chips (e.g. "Lanchas") go to the text search of that name on the same type. */
export const shortcutUrl = (label: string, type: SearchType): string => buildTextSearchUrl(label, type);
